import { Spinner } from '../../components/spinner/spinner';
import { addStyles, DefaultTheme, rgba } from '../../components/theme';
import { BLACK, WHITE } from '../../components/theme.palette';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import type { CaptureRegion } from './map.print.capture';

const CSS = {
	overlay: 'c-MapPrintFrame',
	frame: 'c-MapPrintFrame__frame',
	toolbar: 'c-MapPrintFrame__toolbar',
	hint: 'c-MapPrintFrame__hint',
	counter: 'c-MapPrintFrame__counter',
	busy: 'c-MapPrintFrame__busy',
	button: 'c-MapPrintFrame__button',
	primary: 'c-MapPrintFrame__button--primary',
};

addStyles({
	// The overlay spans the map but is click-through, so the map underneath stays pannable/zoomable.
	// Only the toolbar re-enables pointer events.
	[CSS.overlay]: {
		position: 'absolute',
		inset: '0',
		zIndex: '150',
		pointerEvents: 'none',
	},
	// The clear print area. Its huge spread box-shadow dims everything outside the frame in one shape.
	[CSS.frame]: {
		position: 'absolute',
		boxSizing: 'border-box',
		border: `2px solid ${DefaultTheme.color.accent}`,
		boxShadow: `0 0 0 9999px ${rgba(BLACK, 0.45)}`,
	},
	[CSS.toolbar]: {
		position: 'absolute',
		bottom: DefaultTheme.space(4),
		left: '50%',
		transform: 'translateX(-50%)',
		display: 'flex',
		alignItems: 'center',
		gap: DefaultTheme.space(2),
		padding: `${DefaultTheme.space(2)} ${DefaultTheme.space(3)}`,
		borderRadius: '4px',
		background: rgba(BLACK, 0.82),
		boxShadow: DefaultTheme.shadow.box.dp4,
		pointerEvents: 'auto',
		whiteSpace: 'nowrap',
	},
	[CSS.hint]: {
		color: rgba(WHITE),
		fontSize: '13px',
		marginRight: DefaultTheme.space(2),
	},
	[CSS.counter]: {
		color: rgba(WHITE),
		fontSize: '13px',
		fontWeight: 500,
		marginRight: DefaultTheme.space(2),
	},
	[CSS.busy]: {
		display: 'flex',
		alignItems: 'center',
		gap: DefaultTheme.space(2),
		color: rgba(WHITE),
		fontSize: '13px',
		fontWeight: 500,
		marginRight: DefaultTheme.space(2),
	},
	[CSS.button]: {
		border: 'none',
		borderRadius: '4px',
		padding: `${DefaultTheme.space(1)} ${DefaultTheme.space(3)}`,
		fontSize: '13px',
		fontWeight: 500,
		cursor: 'pointer',
		background: rgba(WHITE, 0.16),
		color: rgba(WHITE),
	},
	[CSS.primary]: {
		background: DefaultTheme.color.accent,
		color: rgba(WHITE),
	},
});

export interface MapPrintFrameOptions {
	region: CaptureRegion | null;
	/** How many frames have been banked via "Add page"; shown as a counter and in the export label. */
	pageCount: number;
	/** True while a frame is being rasterized; shows a spinner and disables the toolbar buttons. */
	busy: boolean;
	onAddPage: () => void;
	onExport: () => void;
	onCancel: () => void;
}

/** Fixed-aspect print frame drawn over the live map, with an add-page / export / cancel toolbar. */
export const MapPrintFrame = {
	view(opts: MapPrintFrameOptions): MithrilVNode {
		if (!opts.region) {
			return null;
		}
		const { x, y, width, height } = opts.region;
		// With pages banked, export those; otherwise the export button does a one-shot capture of the
		// current frame, so it reads "Export to PDF" rather than a count.
		const exportLabel =
			opts.pageCount > 0
				? Translate(T.TOPO_PRINT_EXPORT_COUNT, { count: opts.pageCount })
				: Translate(T.TOPO_PRINT_EXPORT);

		return m('div', { className: CSS.overlay }, [
			m('div', {
				className: CSS.frame,
				style: { left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px` },
			}),
			m('div', { className: CSS.toolbar }, [
				status(opts),
				button(CSS.button, Translate(T.CANCEL), opts.busy, () => opts.onCancel()),
				button(CSS.button, Translate(T.TOPO_PRINT_ADD_PAGE), opts.busy, () => opts.onAddPage()),
				button(`${CSS.button} ${CSS.primary}`, exportLabel, opts.busy, () => opts.onExport()),
			]),
		]);
	},
};

/** Left side of the toolbar: a spinner while capturing, the page counter once pages are banked, or
 *  the framing hint otherwise. */
function status(opts: MapPrintFrameOptions): MithrilVNode {
	if (opts.busy) {
		return m('span', { className: CSS.busy }, [Spinner.view(), Translate(T.TOPO_PRINT_CAPTURING_FRAME)]);
	}
	if (opts.pageCount > 0) {
		return m('span', { className: CSS.counter }, Translate(T.TOPO_PRINT_PAGES_ADDED, { count: opts.pageCount }));
	}
	return m('span', { className: CSS.hint }, Translate(T.TOPO_PRINT_FRAME_HINT));
}

function button(className: string, label: string, disabled: boolean, onclick: () => void): MithrilVNode {
	return m(
		'button',
		{ className, type: 'button', disabled, style: { opacity: disabled ? '0.5' : '1' }, onclick: () => onclick() },
		label
	);
}
